import React, {
    useCallback,
    useEffect,
    useMemo,
} from "react";

import {
    FiAlertCircle,
    FiArrowDownCircle,
    FiArrowUpCircle,
    FiBox,
    FiLoader,
    FiRefreshCw,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchSaleInvoiceStockMovements,
    saleInvoiceStockErrorSelector,
    saleInvoiceStockIncludeReversalsSelector,
    saleInvoiceStockLoadingSelector,
    saleInvoiceStockMovementsSelector,
    saleInvoiceStockSummarySelector,
    setIncludeSaleInvoiceStockReversals,
} from "../../../../../../../store/billing/saleInvoiceStockSlice";

import "./SaleInvoiceStockMovements.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const parsedValue =
        Number(value);

    return Number.isFinite(
        parsedValue
    )
        ? parsedValue
        : fallback;
};

const formatQuantity = (
    value
) => {
    const quantity =
        toNumber(value);

    return new Intl.NumberFormat(
        "en-IN",
        {
            minimumFractionDigits:
                Number.isInteger(
                    quantity
                )
                    ? 0
                    : 2,

            maximumFractionDigits:
                3,
        }
    ).format(
        quantity
    );
};

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        toNumber(value)
    );

const formatDate = (
    value,
    includeTime = false
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        includeTime
            ? {
                day:
                    "2-digit",

                month:
                    "short",

                year:
                    "numeric",

                hour:
                    "2-digit",

                minute:
                    "2-digit",
            }
            : {
                day:
                    "2-digit",

                month:
                    "short",

                year:
                    "numeric",
            }
    ).format(date);
};

const formatLabel = (
    value
) => {
    if (!value) {
        return "-";
    }

    return String(value)
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );
};

const isStockOutMovement = (
    movement
) =>
    String(
        movement
            ?.movementType ||
        ""
    ).toLowerCase() ===
    "sale";

const isStockInMovement = (
    movement
) =>
    String(
        movement
            ?.movementType ||
        ""
    ).toLowerCase() ===
    "sale_cancelled";

const getMovementClass = (
    movement
) => {
    if (
        isStockInMovement(
            movement
        )
    ) {
        return "sale-invoice-stock-status-in";
    }

    return "sale-invoice-stock-status-out";
};

const getMovementLabel = (
    movement
) => {
    if (
        isStockInMovement(
            movement
        )
    ) {
        return "Stock Restored";
    }

    if (
        isStockOutMovement(
            movement
        )
    ) {
        return "Stock Deducted";
    }

    return formatLabel(
        movement
            ?.movementType
    );
};

const getMovementIcon = (
    movement
) => {
    if (
        isStockInMovement(
            movement
        )
    ) {
        return (
            <FiArrowUpCircle />
        );
    }

    return (
        <FiArrowDownCircle />
    );
};

const getProductName = (
    movement
) =>
    movement
        ?.product
        ?.name ||
    "Unknown Product";

const getProductSku = (
    movement
) =>
    movement
        ?.product
        ?.sku ||
    "-";

const getMovementKey = (
    movement,
    index
) =>
    movement
        ?._id ||
    `${movement?.product?._id || "product"}-${movement?.movementType || "movement"}-${index}`;

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SaleInvoiceStockMovements =
    ({
        invoiceId,
        inventoryPostingStatus =
        "",
    }) => {
        const dispatch =
            useDispatch();

        const movements =
            useSelector(
                saleInvoiceStockMovementsSelector
            ) || [];

        const summary =
            useSelector(
                saleInvoiceStockSummarySelector
            ) || {};

        const loading =
            useSelector(
                saleInvoiceStockLoadingSelector
            );

        const error =
            useSelector(
                saleInvoiceStockErrorSelector
            ) || "";

        const includeReversals =
            useSelector(
                saleInvoiceStockIncludeReversalsSelector
            );

        const loadMovements =
            useCallback(
                async () => {
                    if (
                        !invoiceId
                    ) {
                        return;
                    }

                    await dispatch(
                        fetchSaleInvoiceStockMovements({
                            invoiceId,

                            includeReversals,
                        })
                    );
                },
                [
                    dispatch,
                    includeReversals,
                    invoiceId,
                ]
            );

        useEffect(() => {
            loadMovements();
        }, [loadMovements]);

        const stockOutMovements =
            useMemo(
                () =>
                    movements.filter(
                        isStockOutMovement
                    ),
                [movements]
            );

        const stockInMovements =
            useMemo(
                () =>
                    movements.filter(
                        isStockInMovement
                    ),
                [movements]
            );

        const calculatedTotalOut =
            useMemo(
                () =>
                    stockOutMovements.reduce(
                        (
                            total,
                            movement
                        ) =>
                            total +
                            toNumber(
                                movement
                                    ?.quantity
                            ),
                        0
                    ),
                [stockOutMovements]
            );

        const calculatedTotalIn =
            useMemo(
                () =>
                    stockInMovements.reduce(
                        (
                            total,
                            movement
                        ) =>
                            total +
                            toNumber(
                                movement
                                    ?.quantity
                            ),
                        0
                    ),
                [stockInMovements]
            );

        const totalQuantitySold =
            summary
                ?.totalQuantitySold !==
                undefined
                ? toNumber(
                    summary
                        .totalQuantitySold
                )
                : calculatedTotalOut;

        const totalQuantityRestored =
            summary
                ?.totalQuantityRestored !==
                undefined
                ? toNumber(
                    summary
                        .totalQuantityRestored
                )
                : calculatedTotalIn;

        const netQuantityOut =
            summary
                ?.netQuantityOut !==
                undefined
                ? toNumber(
                    summary
                        .netQuantityOut
                )
                : Math.max(
                    totalQuantitySold -
                    totalQuantityRestored,
                    0
                );

        const handleIncludeReversals =
            (
                event
            ) => {
                dispatch(
                    setIncludeSaleInvoiceStockReversals(
                        event
                            .target
                            .checked
                    )
                );
            };

        return (
            <section className="sale-invoice-stock">
                <header className="sale-invoice-stock-header">
                    <div className="sale-invoice-stock-heading">
                        <span>
                            <FiBox />
                        </span>

                        <div>
                            <h3>
                                Stock Movements
                            </h3>

                            <p>
                                Inventory deductions and reversals created for this invoice.
                            </p>
                        </div>
                    </div>

                    <div className="sale-invoice-stock-actions">
                        <label className="sale-invoice-stock-toggle">
                            <input
                                type="checkbox"
                                checked={
                                    includeReversals
                                }
                                disabled={
                                    loading
                                }
                                onChange={
                                    handleIncludeReversals
                                }
                            />

                            <span>
                                Include reversals
                            </span>
                        </label>

                        <button
                            type="button"
                            className="sale-invoice-stock-refresh"
                            disabled={
                                loading ||
                                !invoiceId
                            }
                            onClick={
                                loadMovements
                            }
                        >
                            <FiRefreshCw
                                className={
                                    loading
                                        ? "sale-invoice-stock-spin"
                                        : ""
                                }
                            />

                            Refresh
                        </button>
                    </div>
                </header>

                <div className="sale-invoice-stock-posting-status">
                    <span>
                        Inventory Posting Status
                    </span>

                    <strong
                        className={`sale-invoice-stock-posting-${String(
                            inventoryPostingStatus ||
                            "PENDING"
                        ).toLowerCase()}`}
                    >
                        {formatLabel(
                            inventoryPostingStatus ||
                            "PENDING"
                        )}
                    </strong>
                </div>

                <div className="sale-invoice-stock-summary">
                    <div>
                        <span>
                            Movement Entries
                        </span>

                        <strong>
                            {summary
                                ?.movementCount ??
                                movements.length}
                        </strong>
                    </div>

                    <div className="sale-invoice-stock-summary-out">
                        <span>
                            Quantity Deducted
                        </span>

                        <strong>
                            {formatQuantity(
                                totalQuantitySold
                            )}
                        </strong>
                    </div>

                    <div className="sale-invoice-stock-summary-in">
                        <span>
                            Quantity Restored
                        </span>

                        <strong>
                            {formatQuantity(
                                totalQuantityRestored
                            )}
                        </strong>
                    </div>

                    <div className="sale-invoice-stock-summary-net">
                        <span>
                            Net Quantity Out
                        </span>

                        <strong>
                            {formatQuantity(
                                netQuantityOut
                            )}
                        </strong>
                    </div>
                </div>

                {error && (
                    <div className="sale-invoice-stock-error">
                        <FiAlertCircle />

                        <span>
                            {error}
                        </span>

                        <button
                            type="button"
                            disabled={
                                loading
                            }
                            onClick={
                                loadMovements
                            }
                        >
                            Retry
                        </button>
                    </div>
                )}

                <div className="sale-invoice-stock-table-wrap">
                    <table className="sale-invoice-stock-table">
                        <thead>
                            <tr>
                                <th>
                                    Product
                                </th>

                                <th>
                                    Movement
                                </th>

                                <th>
                                    Date
                                </th>

                                <th className="sale-invoice-stock-text-right">
                                    Quantity
                                </th>

                                <th className="sale-invoice-stock-text-right">
                                    Previous Stock
                                </th>

                                <th className="sale-invoice-stock-text-right">
                                    New Stock
                                </th>

                                <th className="sale-invoice-stock-text-right">
                                    Purchase Price
                                </th>

                                <th>
                                    Performed By
                                </th>

                                <th>
                                    Notes
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading &&
                                movements.length ===
                                0 ? (
                                <tr>
                                    <td
                                        colSpan="9"
                                        className="sale-invoice-stock-loading"
                                    >
                                        <FiLoader className="sale-invoice-stock-spin" />

                                        Loading stock movements...
                                    </td>
                                </tr>
                            ) : movements.length ===
                                0 ? (
                                <tr>
                                    <td
                                        colSpan="9"
                                        className="sale-invoice-stock-empty"
                                    >
                                        <FiBox />

                                        <strong>
                                            No stock movements found
                                        </strong>

                                        <span>
                                            Draft invoices and invoices containing only manual or service items do not create stock movements.
                                        </span>
                                    </td>
                                </tr>
                            ) : (
                                movements.map(
                                    (
                                        movement,
                                        index
                                    ) => (
                                        <tr
                                            key={getMovementKey(
                                                movement,
                                                index
                                            )}
                                        >
                                            <td>
                                                <strong>
                                                    {getProductName(
                                                        movement
                                                    )}
                                                </strong>

                                                <small>
                                                    SKU:{" "}
                                                    {getProductSku(
                                                        movement
                                                    )}
                                                </small>

                                                <small>
                                                    Current stock:{" "}
                                                    {formatQuantity(
                                                        movement
                                                            ?.product
                                                            ?.currentStock
                                                    )}{" "}
                                                    {movement
                                                        ?.product
                                                        ?.unit ||
                                                        ""}
                                                </small>
                                            </td>

                                            <td>
                                                <span
                                                    className={`sale-invoice-stock-status ${getMovementClass(
                                                        movement
                                                    )}`}
                                                >
                                                    {getMovementIcon(
                                                        movement
                                                    )}

                                                    {getMovementLabel(
                                                        movement
                                                    )}
                                                </span>
                                            </td>

                                            <td>
                                                {formatDate(
                                                    movement
                                                        ?.createdAt,
                                                    true
                                                )}
                                            </td>

                                            <td className="sale-invoice-stock-text-right sale-invoice-stock-quantity">
                                                {formatQuantity(
                                                    movement
                                                        ?.quantity
                                                )}
                                            </td>

                                            <td className="sale-invoice-stock-text-right">
                                                {formatQuantity(
                                                    movement
                                                        ?.previousQty
                                                )}
                                            </td>

                                            <td className="sale-invoice-stock-text-right">
                                                {formatQuantity(
                                                    movement
                                                        ?.newQty
                                                )}
                                            </td>

                                            <td className="sale-invoice-stock-text-right">
                                                {formatCurrency(
                                                    movement
                                                        ?.purchasePrice
                                                )}
                                            </td>

                                            <td>
                                                <strong>
                                                    {movement
                                                        ?.performedBy
                                                        ?.name ||
                                                        "-"}
                                                </strong>

                                                <small>
                                                    {movement
                                                        ?.performedBy
                                                        ?.mobile ||
                                                        movement
                                                            ?.performedBy
                                                            ?.email ||
                                                        ""}
                                                </small>
                                            </td>

                                            <td>
                                                <span className="sale-invoice-stock-notes">
                                                    {movement
                                                        ?.notes ||
                                                        "-"}
                                                </span>
                                            </td>
                                        </tr>
                                    )
                                )
                            )}
                        </tbody>
                    </table>
                </div>

                <footer className="sale-invoice-stock-footer">
                    <span>
                        Showing{" "}
                        <strong>
                            {movements.length}
                        </strong>{" "}
                        stock movement records
                    </span>

                    {Boolean(
                        summary
                            ?.isStockReversed
                    ) && (
                            <strong className="sale-invoice-stock-reversed-label">
                                Invoice stock has been reversed
                            </strong>
                        )}
                </footer>
            </section>
        );
    };

export default
    SaleInvoiceStockMovements;